import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick } from 'vue';

type TimeUnit = 'hours' | 'minutes' | 'seconds';
type TimeObject = { hours?: number | string; minutes?: number | string; seconds?: number | string };
type TimeValue = string | Date | TimeObject | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'BaseTimePicker',
  props: {
    value: null,
    format: null,
    only: null,
    returnObject: { type: Boolean }
  },
  emits: ['change'],
  setup(__props: any, { emit: emits }) {

const props = __props as { value?: TimeValue, format?: 'HH:mm:ss' | 'HH:mm' | 'HH' | 'mm' | 'ss', only?: 'hours' | 'minutes' | 'seconds', returnObject?: boolean }





const showHours = computed(() => ['HH:mm:ss', 'HH:mm', 'HH'].includes(props.format));
const showMinutes = computed(() => ['HH:mm:ss', 'HH:mm', 'mm' ].includes(props.format));
const showSeconds = computed(() => ['HH:mm:ss', 'ss' ].includes(props.format));

const getTimeOptions = computed<{ hours: number[], minutes: number[], seconds: number[] }>(() => {
    const options = {
        hours: Array.from({ length: 24 }, (_, i) => i),
        minutes: Array.from({ length: 60 }, (_, i) => i),
        seconds: Array.from({ length: 60 }, (_, i) => i),
    };
    if (props.only === 'hours') {
        options.minutes = [0];
        options.seconds = [0];
    }

    if (props.only === 'minutes') {
        options.hours = [0];
        options.seconds = [0];
    }

    if (props.only === 'seconds') {
        options.hours = [0];
        options.minutes = [0];
    }

    return options;
});

const selectedHour = ref(0);
const selectedMinute = ref(0);
const selectedSecond = ref(0);

const hourColumn = ref<HTMLElement | null>(null);
const minuteColumn = ref<HTMLElement | null>(null);
const secondColumn = ref<HTMLElement | null>(null);

const isDragging = ref(false);
const startY = ref(0);
const startScrollTop = ref(0);
const activeColumn = ref<TimeUnit | null>(null);
const scrollTimeout = ref<number | null>(null);
const isPotentialClick = ref(true);

const formatNumber = (num: number): string => {
    return num < 10 ? `0${num}` : num.toString();
};

const parseTime = (time?: TimeValue): { hours: number; minutes: number; seconds: number } => {
    if (!time) {
        const now = new Date();
        return {
            hours: now.getHours(),
            minutes: now.getMinutes(),
            seconds: now.getSeconds(),
        };
    }

    if (typeof time === 'string') {
        const parts = time.split(':').map(part => parseInt(part) || 0);

        switch (props.format) {
        case 'HH:mm':
            return { hours: parts[0], minutes: parts[1], seconds: 0 };
        case 'HH':
            return { hours: parts[0], minutes: 0, seconds: 0 };
        case 'mm':
            return { hours: 0, minutes: parts[0], seconds: 0 };
        case 'ss':
            return { hours: 0, minutes: 0, seconds: parts[0] };
        default:
            return { hours: parts[0], minutes: parts[1], seconds: parts[2] };
        }
    }

    if (time instanceof Date) {
        return {
            hours: time.getHours(),
            minutes: time.getMinutes(),
            seconds: time.getSeconds(),
        };
    }

    if (typeof time === 'object' && time !== null) {
        return {
            hours: parseInt(props.value.hours) || 0,
            minutes: parseInt(props.value.minutes) || 0,
            seconds: parseInt(props.value.seconds) || 0,
        };
    }

    return { hours: 0, minutes: 0, seconds: 0 };
};

const updateTime = (): void => {
    if (props.returnObject) {
        const time: { hours?: string, minutes?: string, seconds?: string } = {};
        if (showHours.value) time.hours = formatNumber(selectedHour.value);
        if (showMinutes.value) time.minutes = formatNumber(selectedMinute.value);
        if (showSeconds.value) time.seconds = formatNumber(selectedSecond.value);

        emits('change', time);
    } else {
        let timeStr = '';
        if (showHours.value) timeStr += formatNumber(selectedHour.value);
        if (showMinutes.value) timeStr += (timeStr ? ':' : '') + formatNumber(selectedMinute.value);
        if (showSeconds.value) timeStr += (timeStr ? ':' : '') + formatNumber(selectedSecond.value);

        emits('change', timeStr);
    }
};

const scrollToSelected = (column: TimeUnit, behavior: ScrollBehavior = 'auto'): void => {
    const element =
        column === 'hours' ? hourColumn.value :
            column === 'minutes' ? minuteColumn.value :
                secondColumn.value;

    if (!element) return;

    const selectedValue =
        column === 'hours' ? selectedHour.value :
            column === 'minutes' ? selectedMinute.value :
                selectedSecond.value;

    element.scrollTo({
        top: selectedValue * 40,
        behavior,
    });
};

const handleScroll = (column: TimeUnit): void => {
    if (isDragging.value) return;

    const element =
        column === 'hours' ? hourColumn.value :
            column === 'minutes' ? minuteColumn.value :
                secondColumn.value;

    if (!element) return;

    if (scrollTimeout.value) {
        clearTimeout(scrollTimeout.value);
    }

    scrollTimeout.value = setTimeout(() => {
        const scrollTop = element.scrollTop;
        const selectedValue = Math.round(scrollTop / 40);

        if (column === 'hours') selectedHour.value = Math.max(0, Math.min(23, selectedValue));
        if (column === 'minutes') selectedMinute.value = Math.max(0, Math.min(59, selectedValue));
        if (column === 'seconds') selectedSecond.value = Math.max(0, Math.min(59, selectedValue));

        updateTime();
        scrollToSelected(column);
    }, 100) as unknown as number;
};

const startDrag = (e: MouseEvent | TouchEvent, column: TimeUnit): void => {
    isPotentialClick.value = true;
    isDragging.value = false;
    activeColumn.value = column;
    startY.value = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;

    const element =
        column === 'hours' ? hourColumn.value :
            column === 'minutes' ? minuteColumn.value :
                secondColumn.value;

    if (element) {
        startScrollTop.value = element.scrollTop;
        element.style.scrollSnapType = 'none';
    }

    if (e instanceof MouseEvent) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', stopDrag);
    }
};

const handleMouseMove = (e: MouseEvent): void => {
    const moveY = e.clientY;
    const deltaY = Math.abs(moveY - startY.value);

    if (deltaY > 5) {
        isDragging.value = true;
        isPotentialClick.value = false;
        onDrag(e);
    }
};

const onDrag = (e: MouseEvent | TouchEvent): void => {
    if (!isDragging.value || !activeColumn.value) return;

    const y = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
    const deltaY = y - startY.value;

    const element =
        activeColumn.value === 'hours' ? hourColumn.value :
            activeColumn.value === 'minutes' ? minuteColumn.value :
                secondColumn.value;

    if (element) {
        element.scrollTop = startScrollTop.value - deltaY;
        updateSelectedValueFromScroll(activeColumn.value, element);
    }
};

const stopDrag = (): void => {
    if (!activeColumn.value) return;

    const element =
        activeColumn.value === 'hours' ? hourColumn.value :
            activeColumn.value === 'minutes' ? minuteColumn.value :
                secondColumn.value;

    if (element) {
        snapToNearest(activeColumn.value, element);
    }

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopDrag);

    setTimeout(() => {
        isDragging.value = false;
        isPotentialClick.value = true;
    }, 50);
};

const updateSelectedValueFromScroll = (column: TimeUnit, element: HTMLElement): void => {
    const value = Math.round(element.scrollTop / 40);

    if (column === 'hours') selectedHour.value = Math.max(0, Math.min(23, value));
    if (column === 'minutes') selectedMinute.value = Math.max(0, Math.min(59, value));
    if (column === 'seconds') selectedSecond.value = Math.max(0, Math.min(59, value));

    updateTime();
};

const snapToNearest = (column: TimeUnit, element: HTMLElement): void => {
    const value = Math.round(element.scrollTop / 40);

    if (column === 'hours') selectedHour.value = Math.max(0, Math.min(23, value));
    if (column === 'minutes') selectedMinute.value = Math.max(0, Math.min(59, value));
    if (column === 'seconds') selectedSecond.value = Math.max(0, Math.min(59, value));

    scrollToSelected(column, 'smooth');
    updateTime();
};

const initialize = (): void => {
    const { hours, minutes, seconds } = parseTime(props.value);
    selectedHour.value = hours;
    selectedMinute.value = minutes;
    selectedSecond.value = seconds;

    nextTick(() => {
        if (showHours.value) scrollToSelected('hours');
        if (showMinutes.value) scrollToSelected('minutes');
        if (showSeconds.value) scrollToSelected('seconds');
    });
};

const adjustTime = (column: TimeUnit, value: number): void => {
    if (column === 'hours') selectedHour.value = Math.max(0, Math.min(23, value));
    if (column === 'minutes') selectedMinute.value = Math.max(0, Math.min(59, value));
    if (column === 'seconds') selectedSecond.value = Math.max(0, Math.min(59, value));

    scrollToSelected(column, 'smooth');
    updateTime();
};

const handleOptionClick = (event: MouseEvent, column: TimeUnit, value: number): void => {
    if (isDragging.value || !isPotentialClick.value) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }
    adjustTime(column, value);
};

const handleOptionMouseDown = (): void => {
    isPotentialClick.value = true;
};

watch(() => props.value, initialize, { immediate: true });

return { __sfc: true,props, emits, showHours, showMinutes, showSeconds, getTimeOptions, selectedHour, selectedMinute, selectedSecond, hourColumn, minuteColumn, secondColumn, isDragging, startY, startScrollTop, activeColumn, scrollTimeout, isPotentialClick, formatNumber, parseTime, updateTime, scrollToSelected, handleScroll, startDrag, handleMouseMove, onDrag, stopDrag, updateSelectedValueFromScroll, snapToNearest, initialize, adjustTime, handleOptionClick, handleOptionMouseDown }
}

})